import React from 'react';
import { Group, Select, NumberInput, ActionIcon, Text, Tooltip, Paper } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import ClickableHelpText from './ClickableHelpText';

const PROVIDERS = [
  'maestro', 'mintech', 'blood', 'photon', 'gmgn','tradeviz', 'axiom', 'solsniper', 'banana', 'trojan', 'bonkbot',
  'mevxbot', 'soltradingbot', 'pepeboost', 'lunar', 'sharp', 'cabal',
  'custom', 'dbot', 'bloxroute', 'bloom', 'jito', '0slot'
];

type ProviderKey = typeof PROVIDERS[number];

export interface TradeProvidersState {
  [provider: string]: { min: number | null, max: number | null };
}

interface TradeProvidersFilterProps {
  value: TradeProvidersState;
  onChange: (value: TradeProvidersState) => void;
}

const getLabel = (provider: string, min: number | null, max: number | null) => {
  if (min == null && max == null) return provider;
  let minStr = min != null ? min : '';
  let maxStr = max != null ? max : '';
  return `${provider} (${minStr}-${maxStr})`;
};

const inputStyles = {
  input: {
    backgroundColor: '#161B22',
    border: '1px solid #374151',
    color: '#F9FAFB',
    minHeight: '28px',
    fontSize: '13px',
    textAlign: 'center'
  }
};

export const TradeProvidersFilter: React.FC<TradeProvidersFilterProps> = ({ value, onChange }) => {
  // List of selected providers
  const selectedProviders = Object.keys(value);

  // Providers not yet selected
  const availableProviders = PROVIDERS.filter(p => !selectedProviders.includes(p));

  // Add new provider
  const handleAdd = (provider: string) => {
    onChange({
      ...value,
      [provider]: { min: null, max: null }
    });
  };

  // Remove provider
  const handleRemove = (provider: string) => {
    const newValue = { ...value };
    delete newValue[provider];
    onChange(newValue);
  };

  // Change min/max for provider
  const handleMinChange = (provider: string, min: number | null) => {
    onChange({
      ...value,
      [provider]: { ...value[provider], min }
    });
  };
  const handleMaxChange = (provider: string, max: number | null) => {
    onChange({
      ...value,
      [provider]: { ...value[provider], max }
    });
  };

  return (
    <Paper p="sm" mb="sm" style={{ background: '#181C23', borderRadius: 8, border: '1px solid #23272F' }}>
      <Group mb={8} justify="space-between">
        <Group gap={6} align="center">
          <Text fw={600} size="sm">Trade Providers</Text>
          <ClickableHelpText
            text="Filter by trade provider"
            detailedInfo="Add one or more trade providers and set min/max trades for each. Leave min/max empty to disable filtering for that provider."
          />
        </Group>
        <Select
          placeholder="Add provider..."
          data={availableProviders.map(p => ({ value: p, label: p }))}
          onChange={v => v && handleAdd(v)}
          value={null}
          size="xs"
          style={{ minWidth: 160 }}
          searchable
          nothingFoundMessage="All added"
          styles={{
            input: {
              backgroundColor: '#21262D',
              border: '1px solid #374151',
              color: '#F9FAFB',
              minHeight: '28px',
              fontSize: '13px'
            },
            dropdown: {
              backgroundColor: '#181C23',
              border: '1px solid #23272F'
            },
            option: {
              backgroundColor: '#181C23',
              color: '#F9FAFB',
              '&[dataSelected]': {
                backgroundColor: '#4F46E5'
              },
              '&:hover': {
                backgroundColor: '#21262D'
              }
            }
          }}
        />
      </Group>
      <div>
        {selectedProviders.length === 0 && (
          <Text size="xs" c="dimmed" mb={4}>No providers selected</Text>
        )}
        {selectedProviders.map((provider) => (
          <Group key={provider} mb={6} align="center" gap={8} style={{ flexWrap: 'nowrap' }}>
            <Text size="sm" style={{ minWidth: 90 }}>{provider}</Text>
            <NumberInput
              placeholder="min"
              value={value[provider].min ?? ''}
              onChange={val => handleMinChange(provider, val === '' ? null : Number(val))}
              size="xs"
              min={0}
              max={1000}
              style={{ width: 70 }}
              hideControls 
              styles={{
                input: {
                  backgroundColor: '#161B22',
                  border: '1px solid #374151',
                  color: '#F9FAFB',
                  minHeight: '28px',
                  fontSize: '13px',
                  textAlign: 'center'
                }
              }}
            />
            <NumberInput
              placeholder="max"
              value={value[provider].max ?? ''}
              onChange={val => handleMaxChange(provider, val === '' ? null : Number(val))}
              size="xs"
              min={0}
              max={1000}
              style={{ width: 70 }}
              hideControls 
              styles={{
                input: {
                  backgroundColor: '#161B22',
                  border: '1px solid #374151',
                  color: '#F9FAFB',
                  minHeight: '28px',
                  fontSize: '13px',
                  textAlign: 'center'
                }
              }}
            />
            <Tooltip label="Remove provider" withArrow>
              <ActionIcon color="red" size="sm" variant="subtle" onClick={() => handleRemove(provider)}>
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ))}
      </div>
    </Paper>
  );
};

export default TradeProvidersFilter;
